// src/features/tasks/components/TaskDetailsPage/TaskCompletionReceiptCard.jsx
//
// Dual-signature tasks are completed in three steps: a first signature, a
// second signature from someone else, then completing the task. The card shows
// them as a numbered checklist with the current step highlighted and its one
// action as a clear button, so it's obvious what to click next.
import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Chip, Alert } from '@mui/material';
import {
  HowToReg as HowToRegIcon,
  Check as CheckIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../../i18n/LocalizedDateDistance';
import { format } from 'date-fns';
import SignCompletionReceiptDialog from './SignCompletionReceiptDialog';
import TaskActionButtons from '../TaskActionButtons';
import { useAuth } from '../../../../context/AuthContext';
import { useCompletionReceipt } from '../../hooks/useCompletionReceipt';
import { useStaff } from '../../../staff/hooks/useStaff';
import { parseApiTimestamp } from '../../../../utils/formatters/dateFormatter';

const STEP_SIZE = 32;

// One row of the checklist. status: 'done' | 'active' | 'waiting'
function Step({ number, status, title, isLast, children }) {
  const active = status === 'active';
  return (
    <Box sx={{ display: 'flex', gap: 4 }}>
      {/* Indicator + connecting line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: active ? 4 : 0 }}>
        <Box
          sx={{
            width: STEP_SIZE,
            height: STEP_SIZE,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.875rem',
            ...(status === 'done' && { bgcolor: 'success.main', color: 'success.contrastText' }),
            ...(active && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
            ...(status === 'waiting' && { border: 2, borderColor: 'divider', color: 'text.disabled' }),
          }}
        >
          {status === 'done' ? <CheckIcon sx={{ fontSize: 18 }} /> : number}
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, minHeight: 12, my: 1, bgcolor: status === 'done' ? 'success.main' : 'divider' }} />
        )}
      </Box>

      {/* Content; the current step is a tinted panel so it stands out */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          mb: isLast ? 0 : 3,
          ...(active && {
            p: 4,
            borderRadius: 2,
            bgcolor: 'primary.lighter',
            border: 1,
            borderColor: 'primary.light',
          }),
        }}
      >
        <Typography
          variant="body1"
          fontWeight={600}
          color={status === 'waiting' ? 'text.secondary' : 'text.primary'}
          sx={{ lineHeight: `${STEP_SIZE}px` }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

export default function TaskCompletionReceiptCard({ task, onSignatureAdded, onStateChange, loading: taskLoading, sx }) {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signaturePosition, setSignaturePosition] = useState(null);
  const previousTaskIdRef = React.useRef(null);

  const {
    receipt,
    loading,
    error: apiError,
    fetchReceipt,
    signReceipt,
    clearError,
    clearReceipt,
  } = useCompletionReceipt();

  const { staff: staffMembers, fetchDepartmentStaff } = useStaff();

  useEffect(() => {
    fetchDepartmentStaff({ include_inactive: false });
  }, [fetchDepartmentStaff]);

  // Fetch the receipt when the task changes (clear it only when switching task)
  useEffect(() => {
    const currentTaskId = task?.task_id;
    if (previousTaskIdRef.current !== currentTaskId) {
      clearReceipt();
      previousTaskIdRef.current = currentTaskId;
    }
    if (task?.completion_receipt_id) {
      fetchReceipt(task.task_id);
    }
  }, [task?.task_id, task?.completion_receipt_id, fetchReceipt, clearReceipt]);

  const openSignDialog = (position) => {
    setSignaturePosition(position);
    setDialogOpen(true);
  };

  const handleSubmitSignature = async ({ password, pin, notes, staffMemberId }) => {
    const credential = pin !== undefined ? { pin } : { password };
    const result = await signReceipt(task.task_id, staffMemberId, credential, notes);
    if (result.success) {
      setDialogOpen(false);
      onSignatureAdded?.(result.receipt);
      return { success: true };
    }
    return { success: false, error: result.error };
  };

  if (!task?.requires_dual_signature) return null;

  const first = receipt?.first_signatory;
  const second = receipt?.second_signatory;
  const signaturesDone = receipt?.status === 'COMPLETE';
  const taskDone = task.state === 'COMPLETED';

  const stepStatus = [
    first ? 'done' : 'active',
    second ? 'done' : first ? 'active' : 'waiting',
    taskDone ? 'done' : signaturesDone ? 'active' : 'waiting',
  ];
  const currentStep = stepStatus.indexOf('active') + 1; // 0 when everything is done

  // Staff who already signed can't sign again
  const excludeStaffIds = [first?.staff_member_id, second?.staff_member_id].filter(Boolean);

  const signedLine = (signatory, signedAt, notes) => (
    <>
      <Typography variant="body2" color="text.secondary">
        {t('tasks:completion_receipt.steps.signed_by', {
          name: signatory.name,
          time: format(parseApiTimestamp(signedAt), 'PPp', { locale: getDateLocale(i18n.language) }),
        })}
      </Typography>
      {notes && (
        <Typography variant="body2" sx={{ mt: 1, pl: 3, borderLeft: 2, borderColor: 'divider', color: 'text.secondary' }}>
          {notes}
        </Typography>
      )}
    </>
  );

  const signButton = (position) => (
    <Button
      variant="contained"
      startIcon={<DrawIcon />}
      onClick={() => openSignDialog(position)}
      disabled={loading}
      sx={{ mt: 3 }}
    >
      {t('tasks:completion_receipt.steps.sign_button')}
    </Button>
  );

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 4, sm: 6 },
          borderRadius: 3,
          // Accent while someone needs to act on it
          ...(!taskDone && { borderLeft: 4, borderLeftColor: 'primary.main' }),
          ...sx,
        }}
      >
        <Box display="flex" alignItems="center" gap={3} mb={5}>
          <HowToRegIcon color={taskDone ? 'success' : 'primary'} />
          <Typography variant="h5" fontWeight={700} flex={1}>
            {taskDone ? t('tasks:completion_receipt.steps.title_done') : t('tasks:completion_receipt.steps.title')}
          </Typography>
          <Chip
            size="small"
            color={taskDone ? 'success' : 'primary'}
            label={taskDone
              ? t('tasks:completion_receipt.steps.all_done')
              : t('tasks:completion_receipt.steps.progress', { step: currentStep })}
          />
        </Box>

        {apiError && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={clearError}>
            {apiError}
          </Alert>
        )}

        <Step number={1} status={stepStatus[0]} title={t('tasks:completion_receipt.first_signature')}>
          {first ? signedLine(first, receipt.first_signed_at, receipt.first_signature_notes) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:completion_receipt.steps.first_help')}
              </Typography>
              {signButton('first')}
            </>
          )}
        </Step>

        <Step number={2} status={stepStatus[1]} title={t('tasks:completion_receipt.second_signature')}>
          {second ? signedLine(second, receipt.second_signed_at, receipt.second_signature_notes) : first ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:completion_receipt.steps.second_help', { name: first.name })}
              </Typography>
              {signButton('second')}
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('tasks:completion_receipt.steps.second_waiting')}
            </Typography>
          )}
        </Step>

        <Step number={3} status={stepStatus[2]} title={t('tasks:completion_receipt.steps.complete_title')} isLast>
          {taskDone ? (
            <Typography variant="body2" color="text.secondary">
              {t('tasks:completion_receipt.steps.complete_done')}
            </Typography>
          ) : signaturesDone ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:completion_receipt.steps.complete_help')}
              </Typography>
              {onStateChange && (
                <TaskActionButtons
                  task={{ ...task, completion_receipt_status: 'COMPLETE' }}
                  onStateChange={onStateChange}
                  loading={taskLoading}
                  variant="card"
                  buttonVariant="contained"
                  sx={{ mt: 3, display: 'inline-block', minWidth: 200 }}
                />
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('tasks:completion_receipt.steps.complete_waiting')}
            </Typography>
          )}
        </Step>
      </Paper>

      <SignCompletionReceiptDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmitSignature}
        loading={loading}
        currentUser={user}
        staffMembers={staffMembers}
        signaturePosition={signaturePosition}
        excludeStaffIds={excludeStaffIds}
      />
    </>
  );
}
